import React, { useState } from 'react';
import { NavLink, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { BarChart2, Users, CalendarDays, KeyRound, ScanLine, ClipboardList, LogOut, ChevronLeft, ChevronRight, Menu, Star } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import '../../styles/AdminLayout.css';

const NAV_ITEMS = {
  admin: [
    { to: '/admin/analytics', icon: <BarChart2 size={18} />, label: 'Tableau de bord' },
    { to: '/admin/members',   icon: <Users size={18} />,     label: 'Visiteurs' },
    { to: '/staff/events',    icon: <CalendarDays size={18} />, label: 'Événements' },
    { to: '/admin/users',     icon: <KeyRound size={18} />,  label: 'Comptes' },
    { to: '/guard',           icon: <ScanLine size={18} />,  label: 'Scanner QR' },
  ],
  staff: [
    { to: '/staff/events', icon: <CalendarDays size={18} />, label: 'Événements' },
    { to: '/staff/vips', icon: <Star size={18} />, label: 'VIP planifiés' },
  ],
  security: [
    { to: '/guard',        icon: <ScanLine size={18} />,     label: 'Scanner QR' },
    { to: '/guard/manual', icon: <ClipboardList size={18} />, label: 'Entrée manuelle' },
    { to: '/guard/visitors', icon: <Users size={18} />,      label: 'Tous les visiteurs' },
  ],
};

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const items = NAV_ITEMS[user?.role] || [];
  
  // Dynamically determine the page title from the current route
  const currentItem = items.find(item => location.pathname === item.to);
  const title = currentItem ? currentItem.label : 'Startup Village';

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className={`admin-layout ${collapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Mobile overlay */}
      {mobileOpen && <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />}

      {/* Sidebar */}
      <aside className={`admin-sidebar ${mobileOpen ? 'open' : ''}`}>
        <div className="sidebar-brand">
          <div className="brand-icon">
            <img src="/images/logo.png" alt="Startup Village" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>
          {!collapsed && <span className="brand-text">Startup Village</span>}
        </div>

        <nav className="sidebar-nav">
          {items.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              onClick={() => setMobileOpen(false)}
            >
              <span className="nav-icon">{item.icon}</span>
              {!collapsed && <span className="nav-label">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            {!collapsed && (
              <div className="user-details">
                <span className="user-name">{user?.name || user?.email || 'Admin'}</span>
                <span className="user-role">{user?.role}</span>
              </div>
            )}
          </div>
          <button className="logout-btn" onClick={handleLogout} title="Déconnexion">
            {collapsed ? <LogOut size={18} /> : 'Déconnexion'}
          </button>
        </div>

        <button className="collapse-btn" onClick={() => setCollapsed(!collapsed)} title={collapsed ? 'Expand' : 'Collapse'}>
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </aside>

      {/* Main content */}
      <main className="admin-main">
        <header className="admin-topbar">
          <button className="mobile-menu-btn" onClick={() => setMobileOpen(true)}><Menu size={20} /></button>
          <h1 className="page-title">{title}</h1>
          <div className="topbar-right">
            <span className="topbar-role-badge">{user?.role}</span>
          </div>
        </header>
        <div className="admin-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
